import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2, X, Download, Mail, Phone, Building2, Tag, StickyNote, Clock, User as UserIcon, Calendar } from 'lucide-react';
import { contactsAPI } from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import { useToast } from '../contexts/ToastContext';
import { serverIsoToMillis, formatServerDateTime } from '../utils/datetime';

interface Contact {
  id: string;
  name: string;
  email: string;
  phone?: string;
  label: string;
  company?: string;
  notes?: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

const LABEL_STYLES: Record<string, string> = {
  Customer: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  Active: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  New: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  Lead: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
};

const Contacts: React.FC = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  // Deep link from a ticket's customer block: /contacts?email=…&name=…
  const [searchParams, setSearchParams] = useSearchParams();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLabel, setFilterLabel] = useState<string>('');
  const [filterSource, setFilterSource] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Selection
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [mobilePanel, setMobilePanel] = useState<'list' | 'details'>('list');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    label: 'Lead',
    company: '',
    notes: '',
  });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<string | null>(null);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await contactsAPI.list(filterLabel || undefined);
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [filterLabel]);

  // Keep selectedContact in sync after refetch
  useEffect(() => {
    if (selectedContact) {
      const updated = contacts.find(c => c.id === selectedContact.id);
      if (updated) setSelectedContact(updated);
      else { setSelectedContact(null); setShowDetails(false); setMobilePanel('list'); }
    }
  }, [contacts]);

  // Deep link (?email=&name=) — a ticket's customer block links here. Once the
  // list has loaded: select the matching contact and open its details panel,
  // or, when the customer isn't a contact yet, open Add Contact prefilled from
  // the query. Params are cleared either way so a refresh doesn't reopen it.
  useEffect(() => {
    if (loading) return;
    const email = (searchParams.get('email') || '').trim();
    if (!email) return;
    const name = (searchParams.get('name') || '').trim();
    const match = contacts.find(c => (c.email || '').toLowerCase() === email.toLowerCase());
    if (match) {
      handleSelectContact(match);
    } else {
      setEditingContact(null);
      setFormData({ name, email, phone: '', label: 'Lead', company: '', notes: '' });
      setIsModalOpen(true);
    }
    setSearchParams({}, { replace: true });
  }, [loading, contacts, searchParams]);

  const filteredContacts = contacts.filter(c => {
    const matchesSearch =
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.company || '').toLowerCase().includes(searchTerm.toLowerCase());

    const contactSource = c.metadata?.source || '';
    const isManual = !c.metadata?.auto_saved;
    const matchesSource =
      !filterSource ||
      (filterSource === 'manual' && isManual) ||
      (filterSource !== 'manual' && contactSource === filterSource);

    // An unparseable `created_at` must not slip past an active date filter
    // (a NaN comparison is always false, which would silently drop the bound).
    const ts = serverIsoToMillis(c.created_at);
    const matchesDate = (!dateFrom && !dateTo)
      || (ts !== null
        && (!dateFrom || ts >= new Date(dateFrom).getTime())
        && (!dateTo || ts <= new Date(dateTo + 'T23:59:59').getTime()));

    return matchesSearch && matchesSource && matchesDate;
  });

  // ── Handlers ──

  const handleSelectContact = (contact: Contact) => {
    setSelectedContact(contact);
    setShowDetails(true);
    setMobilePanel('details');
  };

  const handleBackToList = () => {
    setSelectedContact(null);
    setShowDetails(false);
    setMobilePanel('list');
  };

  const handleOpenModal = (contact?: Contact) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        name: contact.name,
        email: contact.email,
        phone: contact.phone || '',
        label: contact.label,
        company: contact.company || '',
        notes: contact.notes || '',
      });
    } else {
      setEditingContact(null);
      setFormData({ name: '', email: '', phone: '', label: 'Lead', company: '', notes: '' });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingContact) {
        await contactsAPI.update(editingContact.id, formData);
        showToast('Contact updated', 'success');
      } else {
        await contactsAPI.create(formData);
        showToast('Contact created', 'success');
      }
      setIsModalOpen(false);
      fetchContacts();
    } catch (error) {
      showToast('Failed to save contact', 'error');
    }
  };

  const handleDelete = async () => {
    if (!contactToDelete) return;
    try {
      await contactsAPI.delete(contactToDelete);
      setDeleteModalOpen(false);
      if (selectedContact?.id === contactToDelete) handleBackToList();
      showToast('Contact deleted', 'success');
      fetchContacts();
    } catch (error) {
      showToast('Failed to delete contact', 'error');
    }
  };

  // ── Export ──

  const exportContacts = (format: 'csv' | 'json') => {
    const source = filteredContacts;
    if (source.length === 0) return;

    let content = '';
    let mimeType = '';
    let ext = '';

    if (format === 'json') {
      content = JSON.stringify(source, null, 2);
      mimeType = 'application/json';
      ext = 'json';
    } else {
      const esc = (v: any) => `"${(v ?? '').toString().replace(/"/g, '""')}"`;
      const headers = ['Name', 'Email', 'Phone', 'Company', 'Label', 'Notes', 'Source', 'Created At', 'Updated At'];
      const rows = source.map(c => [
        c.name,
        c.email,
        c.phone ?? '',
        c.company ?? '',
        c.label,
        c.notes ?? '',
        c.metadata?.source ?? '',
        c.created_at,
        c.updated_at,
      ]);
      content = [headers.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))].join('\n');
      mimeType = 'text/csv';
      ext = 'csv';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contacts_export_${new Date().toISOString().split('T')[0]}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Render ──

  return (
    <div className="flex h-[calc(100vh-80px)] -m-6 overflow-hidden">
      <div className="flex-1 flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-950">

        {/* ── Top bar (always visible) ── */}
        <div className="flex items-center justify-between gap-3 p-3 md:p-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex-shrink-0">
          <div className="min-w-0">
            <h1 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white truncate">Contacts</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">Manage your leads and customers</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => exportContacts('csv')} className="p-1.5 md:p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" title="Export CSV">
              <Download className="w-4 h-4" />
            </button>
            <button onClick={() => exportContacts('json')} className="hidden sm:flex p-1.5 md:p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-xs font-medium" title="Export JSON">
              JSON
            </button>
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center gap-1.5 px-3 py-1.5 md:px-4 md:py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add Contact</span>
            </button>
          </div>
        </div>

        {/* ── Main content area ── */}
        <div className="flex-1 flex overflow-hidden">
          <div className="flex-1 flex overflow-hidden bg-white dark:bg-gray-900 md:m-4 lg:m-6 md:rounded-2xl md:shadow-sm md:border md:border-gray-200 md:dark:border-gray-800">

            {/* ── Contact List Panel ── */}
            <div className={`
              flex flex-col flex-shrink-0
              border-r border-gray-200 dark:border-gray-800
              bg-white dark:bg-gray-900
              transition-all duration-300
              ${mobilePanel === 'list' ? 'flex' : 'hidden'}
              md:flex
              ${showDetails ? 'md:w-80 lg:w-96' : 'w-full md:w-full'}
            `}>
              {/* Filters header */}
              <div className="p-3 md:p-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50 flex-shrink-0 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 relative">
                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search contacts..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                    />
                  </div>
                  <select
                    value={filterLabel}
                    onChange={(e) => setFilterLabel(e.target.value)}
                    className="text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  >
                    <option value="">All Labels</option>
                    <option value="Lead">Lead</option>
                    <option value="Customer">Customer</option>
                    <option value="Active">Active</option>
                    <option value="New">New</option>
                  </select>
                  <select
                    value={filterSource}
                    onChange={(e) => setFilterSource(e.target.value)}
                    className="text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  >
                    <option value="">All Sources</option>
                    <option value="email">Email</option>
                    <option value="chat">Chat</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>
                {/* Date range filter */}
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={e => setDateFrom(e.target.value)}
                    className="flex-1 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 focus:ring-2 focus:ring-primary/20 focus:border-primary min-w-0 outline-none"
                    title="From date"
                  />
                  <span className="text-xs text-gray-400">–</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={e => setDateTo(e.target.value)}
                    className="flex-1 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 focus:ring-2 focus:ring-primary/20 focus:border-primary min-w-0 outline-none"
                    title="To date"
                  />
                  {(dateFrom || dateTo) && (
                    <button
                      onClick={() => { setDateFrom(''); setDateTo(''); }}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                      title="Clear date filter"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {filteredContacts.length} contact{filteredContacts.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {/* List items */}
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex justify-center p-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                  </div>
                ) : filteredContacts.length === 0 ? (
                  <div className="p-8 text-center">
                    <UserIcon className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">No contacts found.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {filteredContacts.map(contact => (
                      <div
                        key={contact.id}
                        onClick={() => handleSelectContact(contact)}
                        className={`group p-3 md:p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${selectedContact?.id === contact.id ? 'bg-primary/5 dark:bg-primary/10 border-l-2 border-primary' : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold flex-shrink-0 text-sm">
                            {contact.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                {contact.name}
                              </span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${LABEL_STYLES[contact.label] || LABEL_STYLES.Lead}`}>
                                {contact.label}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{contact.email}</p>
                            {contact.company && (
                              <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{contact.company}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── Details Panel ── */}
            {showDetails && selectedContact && (
              <div className={`
                flex-1 flex flex-col min-w-0 overflow-y-auto
                bg-gray-50 dark:bg-gray-950
                ${mobilePanel === 'details' ? 'flex' : 'hidden'}
                md:flex
              `}>
                {/* Details header */}
                <div className="p-4 md:p-6 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex-shrink-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <button
                        onClick={handleBackToList}
                        className="md:hidden p-1.5 -ml-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
                        title="Back to list"
                      >
                        <X className="w-4 h-4 rotate-180" />
                      </button>
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg flex-shrink-0">
                        {selectedContact.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{selectedContact.name}</h2>
                        <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium mt-1 ${LABEL_STYLES[selectedContact.label] || LABEL_STYLES.Lead}`}>
                          {selectedContact.label}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => navigate(`/inbox?tab=emails&action=compose&email=${encodeURIComponent(selectedContact.email)}`)}
                        className="p-2 text-gray-500 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                        title="Send Email"
                      >
                        <Mail className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleOpenModal(selectedContact)}
                        className="p-2 text-gray-500 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => { setContactToDelete(selectedContact.id); setDeleteModalOpen(true); }}
                        className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleBackToList}
                        className="hidden md:flex p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                        title="Close"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Details body */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-5">
                  {/* Contact Info Card */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Contact Information</p>
                    <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm space-y-3">
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
                          <a href={`mailto:${selectedContact.email}`} className="text-sm font-medium text-primary hover:underline truncate block">
                            {selectedContact.email}
                          </a>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Phone</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {selectedContact.phone || <span className="text-gray-400 italic">Not provided</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Company</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {selectedContact.company || <span className="text-gray-400 italic">Not provided</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Tag className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Label</p>
                          <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${LABEL_STYLES[selectedContact.label] || LABEL_STYLES.Lead}`}>
                            {selectedContact.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Source & Timestamps */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Activity</p>
                    <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm space-y-3">
                      {selectedContact.metadata?.source && (
                        <div className="flex items-center gap-3">
                          <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Source</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                              {selectedContact.metadata.auto_saved ? 'Auto-captured from ' : ''}{selectedContact.metadata.source}
                            </p>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-3">
                        <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Created</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {selectedContact.created_at ? formatServerDateTime(selectedContact.created_at) : 'Unknown'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Last Updated</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {selectedContact.updated_at ? formatServerDateTime(selectedContact.updated_at) : 'Unknown'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <StickyNote className="w-3.5 h-3.5" />
                      Notes
                    </p>
                    <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
                      {selectedContact.notes ? (
                        <div className="space-y-3">
                          {selectedContact.notes.split('\n---\n').map((note, idx) => (
                            <div key={idx} className={`text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed ${idx > 0 ? 'pt-3 border-t border-gray-100 dark:border-gray-700' : ''}`}>
                              {note}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400 dark:text-gray-500 italic">No notes yet.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Empty state when no contact selected (desktop only) */}
            {!showDetails && (
              <div className="hidden md:flex flex-1 items-center justify-center bg-gray-50 dark:bg-gray-950">
                <div className="text-center">
                  <UserIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Select a contact to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-4 md:p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center flex-shrink-0">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {editingContact ? 'Edit Contact' : 'Add Contact'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input required type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-primary outline-none text-sm" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                <input required type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-primary outline-none text-sm" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone (Optional)</label>
                <input type="tel" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-primary outline-none text-sm" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company (Optional)</label>
                <input type="text" value={formData.company} onChange={e => setFormData({ ...formData, company: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-primary outline-none text-sm" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Label</label>
                <select value={formData.label} onChange={e => setFormData({ ...formData, label: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-primary outline-none text-sm">
                  <option value="Lead">Lead</option>
                  <option value="Customer">Customer</option>
                  <option value="Active">Active</option>
                  <option value="New">New</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes (Optional)</label>
                <textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-primary outline-none text-sm resize-none" placeholder="Add context about this contact..." />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium">
                  {editingContact ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Contact"
        message="Are you sure you want to delete this contact? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
      />
    </div>
  );
};

export default Contacts;
